import { Box, Flex } from '@chakra-ui/react';
import type { SelectOption } from '@diamond/shared/types';
import { Button, DateFilter, Searchbox, Select } from '@diamond/shared/ui';
import AddIcon from '@mui/icons-material/Add';
import type { KeyboardEvent } from 'react';
import type { UseFormRegister } from 'react-hook-form';
import { FormProvider } from 'react-hook-form';

import { CABANG_OPTIONS } from '../constants';
import type { CollectionFilterBarProps, SearchFormFields } from '../types';

const DATE_FILTER_CONFIG = {
  fromField: 'lhc_date-from' as const,
  toField: 'lhc_date-to' as const,
  buttonName: 'Pilih tanggal',
  shortYearFormat: true,
  buttonVariant: 'outline' as const,
};

const SEARCH_CONFIG = {
  placeholder: 'Cari no. LHC',
  buttonText: 'Cari',
};

// Helper Components
function FilterSelect({
  id,
  placeholder,
  options,
  value,
  onChange,
}: {
  id: string;
  placeholder: string;
  options: SelectOption[];
  value: SelectOption | null;
  onChange: (newValue: SelectOption | null) => void;
}) {
  return (
    <Box>
      <Select
        id={id}
        placeholder={placeholder}
        options={options}
        value={value}
        onChange={onChange}
      />
    </Box>
  );
}

function SearchSection({
  register,
  onSearch,
}: {
  register: UseFormRegister<SearchFormFields>;
  onSearch: () => void;
}) {
  const handleKeyPress = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      onSearch();
    }
  };

  return (
    <Flex gap={2} alignItems="center">
      <Searchbox
        register={register}
        placeholder={SEARCH_CONFIG.placeholder}
        onKeyPress={handleKeyPress}
        minW="160px"
      />
      <Button
        variant="outline"
        borderColor="blue.500"
        color="blue.600"
        _hover={{ bg: 'blue.50' }}
        flexShrink={0}
        onClick={onSearch}
      >
        {SEARCH_CONFIG.buttonText}
      </Button>
    </Flex>
  );
}

// Main Component
export function CollectionFilterBar({
  methods,
  searchRegister,
  handleSearch,
  handleDateFilter,
  updateDateParams,
  plantValue,
  onPlantChange,
  plantOptions,
  cabangValue,
  onCabangChange,
  onCreateLHC,
}: CollectionFilterBarProps) {
  return (
    <FormProvider {...methods}>
      <Flex
        w="full"
        gap={3}
        alignItems={{ base: 'stretch', md: 'center' }}
        justifyContent="space-between"
        flexWrap="nowrap"
      >
        {/* Left side: Buat LHC button and filters */}
        <Flex
          gap={3}
          alignItems={{ base: 'stretch', md: 'center' }}
          flexWrap="nowrap"
        >
          {/* Buat LHC Button */}
          <Button
            leftIcon={<AddIcon fontSize="small" />}
            colorScheme="blue"
            bgColor="blue.500"
            color="white"
            variant="solid"
            onClick={onCreateLHC}
          >
            Buat LHC
          </Button>

          {/* Date Filter */}
          <DateFilter
            fromField={DATE_FILTER_CONFIG.fromField}
            toField={DATE_FILTER_CONFIG.toField}
            shortYearFormat={DATE_FILTER_CONFIG.shortYearFormat}
            buttonVariant={DATE_FILTER_CONFIG.buttonVariant}
            buttonName={DATE_FILTER_CONFIG.buttonName}
            onFilterChange={handleDateFilter}
            onCustomDateChange={updateDateParams}
          />

          {/* Cabang Filter */}
          <FilterSelect
            id="cabang"
            placeholder="Cabang"
            options={CABANG_OPTIONS}
            value={cabangValue}
            onChange={onCabangChange}
          />

          {/* Plant Filter */}
          <FilterSelect
            id="plant"
            placeholder="Plant"
            options={plantOptions}
            value={plantValue}
            onChange={onPlantChange}
          />
        </Flex>

        {/* Right side: Search */}
        <SearchSection register={searchRegister} onSearch={handleSearch} />
      </Flex>
    </FormProvider>
  );
}
